/**
 * Ascenseurs (partagé par le jeu, l'éditeur et l'API, sous-chemin
 * `@campus/core/elevators`).
 *
 * Un ascenseur est une zone `map_zones` de type `elevator`, posée comme une
 * porte sur la rangée bloquante d'un mur horizontal (2 × 1 cases : le sprite
 * de 2 × 2 cases couvre la face du mur et sa plinthe, les murs dessous restent
 * et bloquent). Chaque niveau desservi a sa propre zone ; toutes les zones d'un
 * même `group` forment un ascenseur. En s'arrêtant devant, on choisit un étage
 * parmi les autres zones du groupe, sur les cartes où l'on a le droit d'aller,
 * et on arrive devant l'ascenseur de cet étage (spawn `elevatorSpawnName`).
 */

export const ELEVATOR_ZONE_TYPE = 'elevator';

/** Sprites LimeZu (Modern Exteriors, ascenseur du métro), dans le catalogue des portes. */
export const ELEVATOR_SPRITES = {
  off: 'subway_elevator_off',
  up: 'subway_elevator_going_up',
  down: 'subway_elevator_going_down',
} as const;

export const MAX_ELEVATOR_GROUP_LENGTH = 40;
export const MAX_ELEVATOR_LABEL_LENGTH = 60;
export const ELEVATOR_FLOOR_MIN = -5;
export const ELEVATOR_FLOOR_MAX = 50;

const GROUP_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

export interface ElevatorProps {
  /** Ascenseur auquel appartient ce niveau (ex. « copy-camp »). */
  group: string;
  /** Numéro d'étage : ordre de la liste, 0 = rez-de-chaussée. */
  floor: number;
  /** Nom affiché dans la liste (ex. « 1er étage - Travaux en duo »). */
  label: string;
}

/** Un étage proposé par l'ascenseur (réponse de `GET /world/elevators/:group`). */
export interface ElevatorFloor extends ElevatorProps {
  mapSlug: string;
}

/** Propriétés valides d'une zone ascenseur, null sinon (libellé nettoyé). */
export function parseElevatorProps(value: unknown): ElevatorProps | null {
  if (!value || typeof value !== 'object') return null;
  const { group, floor, label } = value as Record<string, unknown>;
  if (typeof group !== 'string' || group.length > MAX_ELEVATOR_GROUP_LENGTH || !GROUP_PATTERN.test(group)) return null;
  if (typeof floor !== 'number' || !Number.isInteger(floor) || floor < ELEVATOR_FLOOR_MIN || floor > ELEVATOR_FLOOR_MAX) return null;
  if (typeof label !== 'string') return null;
  const clean = label.replace(/\s+/g, ' ').trim();
  if (!clean || clean.length > MAX_ELEVATOR_LABEL_LENGTH) return null;
  return { group, floor, label: clean };
}

/** Nom du point d'arrivée devant l'ascenseur d'un groupe, sur chaque carte. */
export function elevatorSpawnName(group: string): string {
  return `ascenseur:${group}`;
}

/** Étages triés du plus bas au plus haut, un seul par carte (le plus bas si doublon). */
export function sortElevatorFloors(floors: ElevatorFloor[]): ElevatorFloor[] {
  const seen = new Set<string>();
  return [...floors]
    .sort((a, b) => a.floor - b.floor || a.mapSlug.localeCompare(b.mapSlug))
    .filter((f) => (seen.has(f.mapSlug) ? false : (seen.add(f.mapSlug), true)));
}

// ── Cabine partagée à l'écran (trajets individuels) ──────────────────────
// Tout le monde sur la carte voit les portes s'ouvrir, les avatars entrer ou
// sortir. Chacun voyage pour soi : un groupe qui monte ensemble ne sort pas
// forcément au même étage. Rien n'est stocké, tout passe par le temps réel.

/** Personnes dans la cabine en même temps ; au-delà, on prend le trajet suivant. */
export const ELEVATOR_CAPACITY = 4;

/** Un embarquement, vu des autres : pas dans la cabine puis fermeture des portes (ms). */
export const ELEVATOR_BOARDING_MS = 1_100;

/** Places dans la cabine (px), depuis le point du seuil : devant, puis derrière. */
export const ELEVATOR_CABIN_SPOTS: ReadonlyArray<{ dx: number; dy: number }> = [
  { dx: -10, dy: 0 }, { dx: 10, dy: 0 }, { dx: -10, dy: -7 }, { dx: 10, dy: -7 },
];

/**
 * Places sur le palier (px), depuis le point d'arrivée devant l'ascenseur
 * (milieu des deux cases du seuil) : d'abord ce point, puis la rangée du
 * dessous, au centre des cases. `cabin` : la place dans la cabine d'où l'on
 * sort pour y aller (même côté).
 */
export const ELEVATOR_LANDING_SPOTS: ReadonlyArray<{ dx: number; dy: number; cabin: number }> = [
  { dx: 0, dy: 0, cabin: 0 },
  { dx: -16, dy: 32, cabin: 2 }, { dx: 16, dy: 32, cabin: 1 },
  { dx: -48, dy: 32, cabin: 2 }, { dx: 48, dy: 32, cabin: 3 },
  { dx: 0, dy: 64, cabin: 1 },
];

/** Distance (px) sous laquelle une place du palier est déjà prise par un avatar. */
export const ELEVATOR_LANDING_TAKEN_PX = 20;

/**
 * Première place libre du palier (index dans ELEVATOR_LANDING_SPOTS) : ni
 * occupée par un avatar, ni refusée par `blocked` (mur, meuble). Toutes prises :
 * la moins occupée, 0 à défaut.
 */
export function chooseLandingSpot(
  origin: { x: number; y: number },
  avatars: ReadonlyArray<{ x: number; y: number }>,
  blocked: (x: number, y: number) => boolean = () => false,
): number {
  for (let i = 0; i < ELEVATOR_LANDING_SPOTS.length; i++) {
    const s = ELEVATOR_LANDING_SPOTS[i]!;
    const x = origin.x + s.dx;
    const y = origin.y + s.dy;
    if (i > 0 && blocked(x, y)) continue;
    if (!avatars.some((a) => Math.hypot(a.x - x, a.y - y) < ELEVATOR_LANDING_TAKEN_PX)) return i;
  }
  return 0;
}

/** Place dans la cabine d'où sort l'avatar qui va sur cette place du palier. */
export function cabinSpotForLanding(landing: number): number {
  return ELEVATOR_LANDING_SPOTS[landing]?.cabin ?? 0;
}
